import React, { useMemo } from 'react';
import './ShareOfVoiceOverTimeChart.scss';
import { Skeleton } from 'primereact/skeleton';
import CustomTooltip from '../../../../../shared/components/common/tooltip/CustomTooltip';
import EmptyPanel from '../../../../../shared/components/empty-panel/EmptyPanel';
import LineChart from '../../../../../shared/components/chart/line-chart/LineChart';
import { useTranslation } from 'react-i18next';
import { buildBucketLabelFormatters } from '../../../../../shared/helpers/date/bucketDateLabels';

interface ShareOfVoiceOverTimeChartProps {
  matrix: (string | number)[][];
  brandColors: { name: string; color: string }[] | null;
  period?: string | null;
  isLoading: boolean;
  hasProjectStatsData: boolean;
  hasAnyPendingQuestions: boolean;
}

const ShareOfVoiceOverTimeChart: React.FC<ShareOfVoiceOverTimeChartProps> = ({
  matrix,
  brandColors,
  period,
  isLoading,
  hasProjectStatsData,
  hasAnyPendingQuestions,
}) => {
  const { t, i18n } = useTranslation();

  const dateLabelFormatters = useMemo(
    () => buildBucketLabelFormatters(period, i18n.language),
    [period, i18n.language]
  );

  // Headers are the column names from the matrix header row (skipping the 'date' cell).
  const headers = useMemo(
    () => (matrix.length > 0 ? (matrix[0].slice(1) as string[]) : []),
    [matrix]
  );

  const hasData = matrix.length > 1;

  // Tighten the Y axis to the data instead of always topping out at 100%:
  // max = highest plotted SoV % across all brand series + 10, capped at 100.
  // Matrix: row 0 = headers, col 0 = date, cols 1+ = per-brand values.
  const maxValue = useMemo(() => {
    let max = 0;
    for (let rowIndex = 1; rowIndex < matrix.length; rowIndex++) {
      const row = matrix[rowIndex];
      if (!row) continue;
      for (let colIndex = 1; colIndex < row.length; colIndex++) {
        const value = Number(row[colIndex]);
        if (!Number.isNaN(value) && value > max) max = value;
      }
    }
    return Math.min(100, Math.ceil(max) + 10);
  }, [matrix]);

  return (
    <div className="stats__panel">
      <div className="page-box__title-action">
        <div className="page-box__title page-box__title--flex">
          {t('shareOfVoiceOverTime')}
          <CustomTooltip tooltipText="shareOfVoiceOverTimeTooltip" addMargin={false} />
        </div>
      </div>
      {!hasProjectStatsData && !hasAnyPendingQuestions && !isLoading ? (
        <div className="stats-box stats-box--without-margin stats-box--chart">
          <div className="stats-box__content stats-box__content--empty">
            <EmptyPanel />
          </div>
        </div>
      ) : !hasData || isLoading ? (
        <div className="stats-box stats-box--without-margin stats-box--chart">
          <div className="stats-box__content">
            <Skeleton className="skeleton--chart">
              <span />
            </Skeleton>
          </div>
        </div>
      ) : (
        <div className="stats-box stats-box--without-margin stats-box--chart">
          <div className="stats-box__content">
            <LineChart
              firstData={matrix}
              headers={headers}
              colorsByName={brandColors ?? undefined}
              formatYAsPercentage
              minValue={0}
              maxValue={maxValue}
              legendType="FLEX_START"
              onLegendClick={() => {}}
              dateAxisFormatter={dateLabelFormatters.axis}
              dateTooltipFormatter={dateLabelFormatters.tooltip}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ShareOfVoiceOverTimeChart;
